import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { preventDefault } from '#modules/browser';
import { KeyCode } from './constants';
import { useAutoId, toEventObject } from './helpers';
import Highlight from './Highlight';

TimezoneInput.propTypes = {
  data: PropTypes.array.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
export default function TimezoneInput({
  data,
  label,
  disabled = false,
  name,
  value,
  onChange,
}) {
  const { t } = useTranslation();
  const autoId = useAutoId('timezoneinput');

  const [focused, setFocused] = useState(false);
  const handleFocusInput = () => setFocused(true);
  const handleBlurInput = () => {
    setFocused(false);
    setInputValue(findTimezoneTitle(data, value));
    setSearchedData([]);
  };

  const [inputValue, setInputValue] = useState('');
  const handleChangeInput = (event) => setInputValue(event.target.value);
  const handleMouseDownReset = () => setInputValue('');

  const [searchedData, setSearchedData] = useState([]);
  useEffect(() => {
    if (inputValue === '') return setSearchedData([]);

    const lowerInputValue = inputValue.toLowerCase();
    setSearchedData(
      data.filter(([, , [name, engName]]) => {
        return (
          name.toLowerCase().includes(lowerInputValue) ||
          engName?.toLowerCase().includes(lowerInputValue)
        );
      }),
    );
  }, [data, inputValue]);

  const inputRef = useRef();
  const handleClickSearch = () => {
    inputRef.current.focus();
  };

  const handleMouseDownItem = (id) => () => {
    setSearchedData([]);
    onChange(toEventObject(name, id));
  };
  useEffect(() => {
    setInputValue(findTimezoneTitle(data, value));
  }, [data, value]);

  const listElemRef = useRef();
  const [focusedIndex, setFocusedIndex] = useState(0);
  useEffect(() => {
    if (_.isEmpty(searchedData)) return;

    const listElem = listElemRef.current;
    const itemsCount = searchedData.length;
    const onKeydown = (event) => {
      const { keyCode } = event;
      if (!Object.values(KeyCode).includes(keyCode)) return;

      const { top: listElemTop, bottom: listElemBottom } =
        listElem.getBoundingClientRect();

      event.preventDefault();

      switch (keyCode) {
        case KeyCode.DOWN: {
          return setFocusedIndex((focusedIndex) => {
            const nextIndex = Math.min(focusedIndex + 1, itemsCount - 1);
            const nextElem = listElem.querySelector(
              `li:nth-child(${nextIndex + 1})`,
            );
            const nextElemBottom = nextElem.getBoundingClientRect().bottom;

            if (nextElemBottom > listElemBottom) {
              listElem.scrollTop += nextElemBottom - listElemBottom;
            }

            return nextIndex;
          });
        }
        case KeyCode.UP: {
          return setFocusedIndex((focusedIndex) => {
            const previousIndex = Math.max(focusedIndex - 1, 0);
            const previousElem = listElem.querySelector(
              `li:nth-child(${previousIndex + 1})`,
            );
            const previousElemTop = previousElem.getBoundingClientRect().top;

            if (previousElemTop < listElemTop) {
              listElem.scrollTop += previousElemTop - listElemTop;
            }

            return previousIndex;
          });
        }
        case KeyCode.ENTER: {
          const index = [...listElem.children].indexOf(
            listElem.querySelector('li.focus'),
          );
          const id = searchedData[index][0];
          setSearchedData([]);
          onChange(toEventObject(name, id));
          return;
        }
      }
    };

    listElem.scrollTop = 0;
    setFocusedIndex(0);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('keydown', onKeydown);
    };
  }, [searchedData]);

  return (
    <div className={classNames('search_cover', { focus: focused })}>
      <div className="lw_search">
        <label htmlFor={autoId} className="blind">
          {label}
        </label>
        <input
          ref={inputRef}
          id={autoId}
          type="text"
          autoComplete="off"
          disabled={disabled}
          value={inputValue}
          onChange={handleChangeInput}
          onFocus={handleFocusInput}
          onBlur={handleBlurInput}
        />
        <button
          type="button"
          className="btn_search"
          onClick={handleClickSearch}
          disabled={disabled}
        >
          <i className="blind">{t('button_search')}</i>
        </button>
        <button
          type="button"
          className="btn_reset"
          onMouseDown={preventDefault(handleMouseDownReset)}
        >
          <i className="blind">{t('button_reset')}</i>
        </button>
      </div>
      {!_.isEmpty(searchedData) && (
        <div className="ly_context ly_autocomplete">
          <ul ref={listElemRef}>
            {searchedData.map(([id, gmt, [name]], index) => (
              <li
                key={id}
                className={classNames('has_thmb', {
                  focus: index === focusedIndex,
                })}
              >
                <a
                  href="#"
                  onMouseDown={preventDefault(handleMouseDownItem(id))}
                >
                  <span className="text" title={`${name} ${gmt}`}>
                    <Highlight original={name} keyword={inputValue} />{' '}
                    <strong>{gmt}</strong>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function findTimezoneTitle(timezones, id) {
  const found = timezones.find((timezone) => timezone[0] === id);
  if (!found) return '';

  const [, gmt, [name]] = found;
  return `${name} ${gmt}`;
}
